import {hide, show} from '../../walkthrough/overlay';
import {toTestId} from '../../utility/testIdUtil';

// A group's row in the Groups sidebar, arrow and name together, so a walkthrough can light up ON-ROLL, Medical and Asthma.
const GROUP_NAME = '[role="treeitem"] > div:first-child';
const PRESSABLE = `button, a, [role="button"], [role="tab"], [role="menuitem"], label, ${GROUP_NAME}`;
// AD-2653: a step can also name a field, a section heading, a table row or a card.
const FIELD_LABEL = 'label, legend, .MuiInputLabel-root, .MuiFormLabel-root';
const FIELD = '.MuiFormControl-root, .MuiTextField-root, .MuiAutocomplete-root';
const HEADING = 'h1, h2, h3, h4, h5, h6, [role="heading"]';
const CELL = 'td, th, [role="cell"], [role="gridcell"]';
const CARD = '.MuiCard-root';
const WAIT_MS = 60000;
const CHECK_MS = 400;
const SETTLE_MS = 1200;
const HEADING_MS = 1500;

/** Anything inside Felicity's own panel or button is never outlined. */
const FELICITY = '[data-felicity]';

const hidden = (element) => {
    if (element.closest('[hidden], [aria-hidden="true"]')) {
        return true;
    }

    const style = element.ownerDocument.defaultView?.getComputedStyle(element);
    return style ? style.display === 'none' || style.visibility === 'hidden' : false;
};

// A button that shows only an icon is named by its label, or else by the icon it shows.
const ICON_NAMES = {
    Edit: 'Edit',
    Delete: 'Delete',
    DeleteOutline: 'Delete',
    Home: 'Home address',
    Visibility: 'View',
    Add: 'Add',
    AddCircle: 'Add',
    AddCircleOutline: 'Add',
    Close: 'Close',
    Download: 'Download',
    FileDownload: 'Download',
    MoreVert: 'More',
    Search: 'Search',
    Settings: 'Settings'
};

const iconName = (element) => {
    const icon = element.querySelector('svg[data-testid$="Icon"]')?.getAttribute('data-testid').replace(/Icon$/, '');
    return icon ? ICON_NAMES[icon] ?? '' : '';
};

export const nameOf = (element) => {
    const text = (element.textContent || '').replace(/\s+/g, ' ').replace(/\s*\*\s*$/, '').trim();

    if (text) {
        return text;
    }

    const label = (element.getAttribute('aria-label') || element.getAttribute('title') || '').trim();

    if (label) {
        return label;
    }

    return iconName(element);
};

const painted = (element) => {
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
};

const best = (candidates) => {
    const shown = candidates.filter(element => !element.closest(FELICITY) && !hidden(element));
    return shown.find(painted) ?? shown[0] ?? null;
};

/**
 * Finds a menu, tab, section or button by the words Felicity used. The words must match the
 * screen exactly, ignoring case, so a name she invents finds nothing rather than the wrong thing.
 */
export const findNamed = (name, root = document) => {
    // A required field's star and a label's closing colon, as on Members:, are left off, whether or not Felicity wrote them.
    const plain = text => String(text ?? '').replace(/\s+/g, ' ').replace(/[\s*:]+$/, '').trim().toLowerCase();
    const wanted = plain(name);

    if (!wanted) {
        return null;
    }

    // The top bar says 'Settings'. Older help text and Felicity's knowledge may still say School Settings.
    const names = wanted === 'school settings' ? [wanted, 'settings'] : [wanted];
    const same = element => names.includes(plain(element.textContent));
    const pressable = Array.from(root.querySelectorAll(PRESSABLE)).filter(element => !element.matches(FIELD_LABEL));
    const byWords = best(pressable.filter(same)) ??
        best(pressable.filter(element => nameOf(element).toLowerCase() === wanted || iconName(element).toLowerCase() === wanted));

    if (byWords) {
        return byWords;
    }

    // A field is outlined whole, found by its label or by the name read out for it.
    // A name written with its star, such as "Students *", is the field to fill in, never an option of the same name.
    const tickOption = label => Boolean(label.querySelector('input[type="radio"], input[type="checkbox"]'));
    const wantsField = /\*\s*$/.test(String(name ?? ''));
    const byLabel = best(Array.from(root.querySelectorAll(FIELD_LABEL)).filter(same).filter(label => !wantsField || !tickOption(label))
        .map(label => label.closest(FIELD) ?? (label.htmlFor ? root.getElementById?.(label.htmlFor) : null) ?? label)
        .concat(Array.from(root.querySelectorAll('input, textarea, [role="combobox"]'))
            .filter(input => (input.getAttribute('aria-label') || '').trim().toLowerCase() === wanted)
            .map(input => input.closest(FIELD) ?? input)));

    if (byLabel) {
        return byLabel;
    }

    const byHeading = best(Array.from(root.querySelectorAll(HEADING)).filter(same));

    if (byHeading) {
        return byHeading;
    }

    // A row or a card is found by the words in one of its cells or lines.
    const byRow = best(Array.from(root.querySelectorAll(CELL)).filter(same).map(cell => (cell.matches('th') ? cell : cell.closest('tr, [role="row"]') ?? cell))
        .concat(Array.from(root.querySelectorAll(`${CARD} *`)).filter(element => element.children.length === 0 && same(element))
            .map(element => element.closest(CARD))));

    if (byRow) {
        return byRow;
    }

    const byName = ['section', 'tab']
        .map(kind => `[data-testid="${toTestId(kind, name)}"]`)
        .flatMap(selector => Array.from(root.querySelectorAll(selector)));

    return best(byName);
};

const SCREEN_LIMIT = 3000;

/**
 * AD-2653: the names of the parts on the person's screen, so Felicity can name the button or field
 * in front of them, as a person sitting beside them would. Only the names of buttons, tabs, menus,
 * field labels, column headings and the title of an open window are taken. What has been typed into
 * a field and the rows of a table stay on the page.
 */
export const describeScreen = (root = document) => {
    const shown = selector => Array.from(root.querySelectorAll(selector))
        .filter(element => !element.closest(FELICITY) && !hidden(element));
    const names = elements => Array.from(new Set(elements.map(nameOf)
        .filter(name => /[a-z]/i.test(name) && name.length <= 40)));
    const dialog = shown('[role="dialog"]').pop();
    const scope = selector => dialog ? Array.from(dialog.querySelectorAll(selector)).filter(element => !hidden(element)) : shown(selector);
    const parts = [
        dialog && `Open window: ${names(Array.from(dialog.querySelectorAll('h2, [class*="DialogTitle"]'))).join(', ')}`,
        `Tabs: ${names(scope('[role="tab"]')).join(', ')}`,
        `Buttons and links: ${names(scope('button, a, [role="button"], [role="menuitem"]')).join(', ')}`,
        // A required field keeps its *, so Felicity knows it must be filled in before the window's button works.
        `Fields: ${Array.from(new Set(scope(FIELD_LABEL).map(label => (label.textContent || '').replace(/\s+/g, ' ').trim())
            .filter(name => /[a-z]/i.test(name) && name.length <= 40))).join(', ')}`,
        `Column headings: ${names(scope('th, [role="columnheader"]')).join(', ')}`,
        // The groups the person can see in the Groups sidebar, so Felicity names them as the screen shows them.
        `Groups sidebar: ${names(scope(GROUP_NAME)).join(', ')}`,
        // Which tick boxes and options are already ticked, so Felicity never asks for one to be ticked twice.
        `Ticked: ${names(scope('label').filter(label => label.querySelector('input[type="checkbox"]:checked, input[type="radio"]:checked'))).join(', ')}`,
        `Not ticked: ${names(scope('label').filter(label => label.querySelector('input[type="checkbox"]:not(:checked), input[type="radio"]:not(:checked)'))).join(', ')}`
    ];
    return parts.filter(part => part && !/: $/.test(part)).join('\n').slice(0, SCREEN_LIMIT);
};

// A group in the Groups sidebar that is already open counts as done, so pressing it again never closes it.
const isOpen = element => Boolean(element?.matches?.('[aria-selected="true"], [aria-current="page"], [aria-current="true"], .Mui-selected, .active') ||
    (element?.matches?.(GROUP_NAME) && element.parentElement.getAttribute('aria-expanded') === 'true'));

// AD-2653: Felicity's words for a step are reliable even when her outline line leaves out the sidebar group they name,
// so the group her words name last, as it shows and still closed in the Groups sidebar, is added to the parts to light.
const SPECIAL = /[.*+?^$()|[\]\\{}]/g;
const wholeName = name => new RegExp('(^|[^\\w-])' + name.replace(SPECIAL, '\\$&') + '(?![\\w-])');

export const withNamedGroup = (steps, words) => {
    const text = String(words ?? '');
    const named = Array.from(document.querySelectorAll(GROUP_NAME))
        .filter(row => !hidden(row) && !row.closest(FELICITY) && !isOpen(row))
        .map(row => nameOf(row))
        .filter(name => name && wholeName(name).test(text))
        .map(name => ({name, at: text.lastIndexOf(name)}))
        .sort((a, b) => b.at - a.at || b.name.length - a.name.length)[0];
    if (!named || steps.some(step => step.toLowerCase() === named.name.toLowerCase())) {
        return steps;
    }
    return [...steps, named.name];
};

const guide = {
    steps: [],
    at: -1,
    element: null,
    others: [],
    timer: null,
    observer: null,
    onClick: null,
    onKey: null,
    walk: null,
    onDone: null,
    doneTimer: null
};

// AD-2653: a walkthrough step is done once its last part is pressed or filled in ("press"), or once
// the next screen opens after it ("open"). onDone is called once, then the guide stops.
const finish = () => {
    const onDone = guide.onDone;
    guide.onDone = null;
    stopGuide();
    onDone?.();
};

const pathname = () => window.location.pathname;

// A record such as a contact or a box opens in a dialog rather than on a new screen.
const dialogs = () => document.querySelectorAll('[role="dialog"]').length;

const waitForNextScreen = (open = dialogs()) => {
    clearInterval(guide.doneTimer);
    const from = pathname();
    guide.doneTimer = setInterval(() => {
        if (pathname() !== from || dialogs() > open) {
            finish();
        }
    }, CHECK_MS);
};

const required = element => /\*\s*$/.test((element.querySelector?.(FIELD_LABEL)?.textContent ?? '').trim()) ||
    Boolean(element.querySelector?.('[required], [aria-required="true"]'));

const isField = element => Boolean(element?.matches?.(FIELD) || element?.querySelector?.('input:not([type="hidden"]), textarea'));

const fieldValue = element => Array.from(element.querySelectorAll('input, textarea'))
    .map(input => (/^(checkbox|radio)$/.test(input.type) ? String(input.checked) : input.value)).join('|');
const isTick = element => Boolean(element.querySelector?.('input[type="checkbox"], input[type="radio"]'));
// A chooser such as Groups shows what was chosen as words inside it, without a typed value.
const shownText = element => (element.textContent || '').replace(/\s+/g, ' ').trim();

// A field step is done once the person has changed the field and moved out of it.
// A list such as Title opens before the click reaches the field, so a field is never moved on by a click.
const waitForField = (element, then = finish) => {
    clearInterval(guide.doneTimer);
    const start = fieldValue(element);
    const startText = shownText(element);
    // A set of options such as Members is done when the person presses one, even the one already chosen.
    let picked = false;
    const radios = Array.from(element.querySelectorAll('input[type="radio"]'));
    radios.forEach(radio => radio.addEventListener('click', () => {
        picked = true;
    }, {once: true}));
    guide.doneTimer = setInterval(() => {
        const value = fieldValue(element);
        // A tick box is done the moment it is ticked or unticked.
        const ticked = (isTick(element) && value !== start) || picked;
        const typed = value !== start && value.replace(/\||false|true/g, '').trim() && !element.contains(document.activeElement);
        const chosen = shownText(element) !== startText && !element.contains(document.activeElement);
        if (ticked || typed || chosen) {
            clearInterval(guide.doneTimer);
            then();
        }
    }, CHECK_MS);
};

const lastStepReached = () => {
    if (!guide.onDone) {
        setTimeout(stopGuide, 0);
    } else if (guide.walk === 'open' && !guide.element?.matches?.(GROUP_NAME)) {
        // A group in the Groups sidebar opens under itself without a new screen, so it is always done when pressed.
        // The person now chooses the record, so the outline on the part already pressed is cleared.
        stopWaiting();
        if (guide.element) {
            hide();
            guide.element = null;
        }
        // The part just pressed may open a screen of its own, such as Profiles. The record is chosen after it.
        // A window the pressed part opens, such as the contact's, counts from the moment it is pressed.
        const open = dialogs();
        guide.doneTimer = setTimeout(() => waitForNextScreen(open), SETTLE_MS);
    } else {
        setTimeout(finish, 0);
    }
};

const stopWaiting = () => {
    guide.observer?.disconnect();
    guide.observer = null;
    clearTimeout(guide.timer);
    clearInterval(guide.timer);
    guide.timer = null;
};

export const stopGuide = () => {
    stopWaiting();
    clearInterval(guide.doneTimer);

    if (guide.onSkip) {
        document.removeEventListener('click', guide.onSkip, true);
        guide.onSkip = null;
    }

    if (guide.onClick) {
        document.removeEventListener('click', guide.onClick, true);
        document.removeEventListener('keydown', guide.onKey, true);
    }

    if (guide.element) {
        hide();
    }

    Object.assign(guide, {steps: [], at: -1, element: null, others: [], onClick: null, onKey: null, walk: null, onDone: null, doneTimer: null});
};

/** Keeps the outline on the step while the page re-renders it, and gives up once it has gone. */
const watch = () => {
    guide.timer = setInterval(() => {
        if (guide.element?.isConnected) {
            return;
        }

        const again = findNamed(guide.steps[guide.at]);

        if (again) {
            guide.element = again;
            guide.others = twins(again);
            show(again, {scroll: false, bright: true, others: guide.others});
        } else {
            stopGuide();
        }
    }, CHECK_MS);
};

// The same button on every row of a table, such as Edit contact, is outlined on every row.
const twins = (element) => {
    if (!element.matches(PRESSABLE) || !element.closest('tr, [role="row"]')) {
        return [];
    }

    const name = nameOf(element).toLowerCase();
    return Array.from(document.querySelectorAll(PRESSABLE))
        .filter(other => other !== element && other.closest('tr, [role="row"]') && !other.closest(FELICITY) && !hidden(other) &&
            nameOf(other).toLowerCase() === name);
};

const outline = (at, element) => {
    stopWaiting();
    clearInterval(guide.doneTimer);
    if (guide.onSkip) {
        document.removeEventListener('click', guide.onSkip, true);
        guide.onSkip = null;
    }
    guide.at = at;
    guide.element = element;
    guide.others = twins(element);
    show(element, {bright: true, others: guide.others});
    watch();

    // A heading, such as a window's title, is shown for a moment, then the next part is outlined.
    if (element.matches(HEADING) && at < guide.steps.length - 1) {
        guide.doneTimer = setTimeout(() => waitFor(at + 1), HEADING_MS);
        return;
    }

    if (!isField(element)) {
        return;
    }

    const last = at === guide.steps.length - 1;
    if (!last) {
        waitForField(element, () => waitFor(at + 1));
    } else if (guide.onDone && guide.walk === 'press') {
        waitForField(element);
    } else {
        return;
    }

    if (!required(element)) {
        // A field that may be left blank, such as Category, is done once the person moves on from it.
        guide.onSkip = (event) => {
            if (!element.contains(event.target) && !event.target.closest?.(FELICITY)) {
                document.removeEventListener('click', guide.onSkip, true);
                guide.onSkip = null;
                clearInterval(guide.doneTimer);
                if (last) {
                    finish();
                } else {
                    waitFor(at + 1);
                }
            }
        };
        document.addEventListener('click', guide.onSkip, true);
    }
};

/**
 * The next step often belongs to a screen that is still loading, or appears only once the person
 * has picked a record, so it is waited for. A minute gives a person time to read the step.
 */
const waitFor = (at) => {
    stopWaiting();
    guide.at = at;

    const found = findNamed(guide.steps[at]);

    if (found) {
        outline(at, found);
        return;
    }

    if (guide.element) {
        hide();
        guide.element = null;
    }

    guide.observer = new MutationObserver(() => {
        const element = findNamed(guide.steps[at]);
        if (element) {
            outline(at, element);
        }
    });
    guide.observer.observe(document.documentElement, {childList: true, subtree: true});
    guide.timer = setTimeout(stopGuide, WAIT_MS);
};

/**
 * Outlines what Felicity named, one step at a time. It starts at the furthest step already on
 * the screen, because a person already in Settings needs no outline on Settings.
 * Pressing the outlined thing moves on to the next step. Escape clears it.
 */
export const startGuide = (steps, {walk = null, onDone = null} = {}) => {
    stopGuide();
    guide.walk = walk;
    guide.onDone = walk ? onDone : null;

    if (!steps?.length) {
        if (guide.onDone && walk === 'open') {
            waitForNextScreen();
        }
        return;
    }

    guide.steps = steps;

    // A step whose part is already open, such as the menu or tab the person is on, is passed over.
    let start = 0;
    while (start < steps.length - 1 && isOpen(findNamed(steps[start]))) {
        start++;
    }
    // A group in the Groups sidebar that is already open is never lit as the last part: pressing it would close it.
    const lastPart = findNamed(steps[start]);
    if (start === steps.length - 1 && lastPart?.matches?.(GROUP_NAME) && isOpen(lastPart)) {
        stopGuide();
        return;
    }

    // A part out of reach, such as a tab behind an open window, is passed over for the next part on the screen.
    if (!findNamed(steps[start])) {
        const reachable = steps.findIndex((step, index) => index > start && findNamed(step));
        start = reachable > -1 ? reachable : start;
    }

    guide.onClick = (event) => {
        if (guide.element && !isField(guide.element) && [guide.element, ...guide.others].some(part => part.contains(event.target))) {
            const next = guide.at + 1;
            if (next < guide.steps.length) {
                setTimeout(() => waitFor(next), 0);
            } else {
                lastStepReached();
            }
        }
    };
    guide.onKey = (event) => {
        if (event.key === 'Escape') {
            stopGuide();
        }
    };
    document.addEventListener('click', guide.onClick, true);
    document.addEventListener('keydown', guide.onKey, true);

    waitFor(start);
};

export const guideStep = () => (guide.element ? {at: guide.at, name: guide.steps[guide.at], element: guide.element} : null);
